import React from 'react';
import { useTranslation } from 'react-i18next';
// Its own one-key namespace, never `../../../i18n`: this is in the initial bundle.
import { NS } from './i18n';
import './EscalationsSkeleton.css';

/** Rule rows drawn in the table — enough to fill a laptop-height card. */
const RULE_ROWS = [0, 1, 2, 3];

const Bone: React.FC<{ className: string }> = ({ className }) => (
  <span className={`escalations-skeleton__bone ${className}`} />
);

/** The rules table: a header row, then one row per rule (name and description, category, priority, status). */
const TableSkeleton: React.FC = () => (
  <div className="escalations-skeleton__table">
    <div className="escalations-skeleton__row escalations-skeleton__row--head">
      <Bone className="escalations-skeleton__cell escalations-skeleton__cell--name" />
      <Bone className="escalations-skeleton__cell escalations-skeleton__cell--short" />
      <Bone className="escalations-skeleton__cell escalations-skeleton__cell--short" />
      <Bone className="escalations-skeleton__cell escalations-skeleton__cell--short" />
    </div>
    {RULE_ROWS.map((row) => (
      <div key={row} className="escalations-skeleton__row">
        <div className="escalations-skeleton__rule">
          <Bone className="escalations-skeleton__line escalations-skeleton__line--rule-name" />
          <Bone className="escalations-skeleton__line escalations-skeleton__line--rule-description" />
        </div>
        <Bone className="escalations-skeleton__tag" />
        <Bone className="escalations-skeleton__tag escalations-skeleton__tag--priority" />
        <Bone className="escalations-skeleton__switch" />
      </div>
    ))}
  </div>
);

export interface EscalationsSkeletonProps {
  /**
   * Adds the agent screens' 24px gutter (`AgentShell` in `AssistantDetailLayout`),
   * for `RouteSkeleton`, which draws it straight inside the app chrome.
   */
  standalone?: boolean;
  /**
   * `table` draws only the rules table, for the page itself while the rules
   * load (the rest of the page is already there).
   */
  part?: 'page' | 'table';
  /**
   * What the status says. The page passes its own translated copy; elsewhere the
   * skeleton's namespace has it.
   */
  label?: string;
}

/**
 * Loading shape of the escalation rules (SL-1944). One shape from a cold load to
 * data: `RouteSkeleton` (inside the app chrome), the agent layouts while the
 * agent loads and the route's Suspense fallback draw this; the page then mounts
 * with the same card, its table drawing the same rows (`part="table"`) while the
 * rules load.
 *
 * The page as it arrives: breadcrumb, then the page card — the module's title
 * and its line below — holding the rules card: its title and line, the add
 * button, the table.
 *
 * Its own classes, never the page's: the page's sheets come with its chunk and
 * this paints before it. Nothing visible but bones; the status carries its
 * translated text inside, visually hidden, so a screen reader announces it. No
 * `aria-busy`: on a live region it tells assistive tech to hold the
 * announcement.
 */
export const EscalationsSkeleton: React.FC<EscalationsSkeletonProps> = ({
  standalone = false,
  part = 'page',
  label,
}) => {
  const { t } = useTranslation(NS);
  const text = label ?? t('loading');

  if (part === 'table') {
    return (
      <div
        className="escalations-skeleton escalations-skeleton--table"
        role="status"
        data-testid="escalations-table-skeleton"
      >
        <span className="escalations-skeleton__sr-only">{text}</span>
        <div aria-hidden="true">
          <TableSkeleton />
        </div>
      </div>
    );
  }

  return (
    <div
      className={`escalations-skeleton${standalone ? ' escalations-skeleton--standalone' : ''}`}
      role="status"
      data-testid="escalations-skeleton"
    >
      <span className="escalations-skeleton__sr-only">{text}</span>
      <div className="escalations-skeleton__header" aria-hidden="true">
        <Bone className="escalations-skeleton__crumb" />
      </div>
      <div className="escalations-skeleton__page-card" aria-hidden="true">
        <Bone className="escalations-skeleton__title" />
        <Bone className="escalations-skeleton__line escalations-skeleton__line--subtitle" />
        <div className="escalations-skeleton__rules-card">
          <div className="escalations-skeleton__rules-head">
            <div className="escalations-skeleton__rules-text">
              <Bone className="escalations-skeleton__line escalations-skeleton__line--rules-title" />
              <Bone className="escalations-skeleton__line escalations-skeleton__line--rules-subtitle" />
            </div>
            <Bone className="escalations-skeleton__button" />
          </div>
          <TableSkeleton />
        </div>
      </div>
    </div>
  );
};
